<script setup lang="ts">
import {computed, type ComputedRef, type PropType} from "vue";
import type {SurveyProgressDenormalizedQuestionAndAnswerModel} from "@/types/model.ts";
import {useI18n} from "vue-i18n";
import {SurveyComponentTypeEnum} from "@/constants/survey.ts";

interface GroupedQuestionAnswerModel {
  ansIds: number[];
  answers: string[];
  pgeId: number;
  qId: number;
  question: string;
}

const props = defineProps({
  revableAnss: {
    type: Array as PropType<SurveyProgressDenormalizedQuestionAndAnswerModel[]>,
    required: true,
    default: []
  }
});

const emit = defineEmits(['edit-clicked']);

const {t} = useI18n();

const grpdQAns: ComputedRef<GroupedQuestionAnswerModel[]> = computed(() => {
  const revableAnssInOrder = props.revableAnss.sort((a, b) => a.page.renderingConfig.order - b.page.renderingConfig.order || a.question.renderingConfig.order - b.question.renderingConfig.order);
  
  let result: GroupedQuestionAnswerModel[] = [];

  revableAnssInOrder.forEach(qA => {
    const hasQ = result.some(x => x.qId === qA.question.questionId),
      thisAns = qA.question.permittedAnswers.find(a => a.answerId === qA.answerId);
    
    // Determine what the answer is.
    let ansVal: string = t('gen_not_provided');
    
    if (!qA.skipped && thisAns) {
      
      switch (thisAns.renderingConfig.type) {
        case SurveyComponentTypeEnum.dropdown:
          const text = thisAns.renderingConfig.dropdownItems?.find(i => i.value.toString() === qA.answerFreeformText)?.text;
          if (text) ansVal = text;
          break;
        default:
          ansVal = thisAns.name;
          break;
      }
    }
    
    // Push questions and answers to `result`.
    if (hasQ) {
      const qIdx = result.findIndex(x => x.qId === qA.question.questionId),
        hasThisAns = thisAns ? result[qIdx]?.ansIds.includes(thisAns.answerId) : false;

      result[qIdx]?.ansIds.push(qA.answerId);
      
      if (!hasThisAns) result[qIdx]?.answers.push(ansVal);
    } else {
      result.push({
        ansIds: [qA.answerId],
        answers: [ansVal],
        pgeId: qA.page.pageId,
        qId: qA.question.questionId,
        question: qA.question.questionHeader
      });
    }
  })
  
  return result;
});

function onEditClicked(toPgeId: number) {
  emit('edit-clicked', toPgeId);
}
</script>

<template>
  
  <!-- Title -->
  <h3 class="mb-16">{{ t('rfb_pmh_summary_title') }}</h3>
  
  <!-- Instructions -->
  <p class="mb-16">{{ t('rfb_pmh_summary_body') }}</p>
  
  <!-- Question/Answer/Edit -->
  <div v-for="(qAns, i) in grpdQAns"
       class="row q-ans-edit"
       :key="i">

    <!-- Question/Answer -->
    <div class="col">
      
      <!-- Question -->
      <div class="row col question">{{ qAns.question }}</div>
      
      <!-- Answer -->
      <div class="row col answer">{{ qAns.answers.join(', ') }}</div>
    </div>
    
    <!-- Edit -->
    <div class="col-auto edit">
      <zbm-btn-icon @click="onEditClicked(qAns.pgeId)"
                    icon="fa-solid fa-pen-circle"
                    size="22px"
                    text-color="primitives-brand-100" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';

.q-ans-edit { // .row
  column-gap: 10px;
  align-items: start;
  padding-block: 12px;
  padding-inline: 16px 12px;
  border: $generic-border;
  border-radius: $generic-border-radius-md;
  
  &:not(:last-child) {
    margin-block-end: 16px;
  }
  
  .question { // .row.col
    font-size: map.get($font-sizes, '12');
    line-height: map.get($font-sizes, '16');
    color: $content-txt-secondary;
  }
  
  .answer { // .row.col
    font-weight: map.get($text-weights, semibold);
    font-size: map.get($font-sizes, '16');
    line-height: map.get($line-heights, '24');
    color: $content-txt-def;
  }
  
  .edit { // .col-auto
    line-height: 1;
  }
}
</style>